import React, { useMemo, useState } from 'react';
import {
  FaArrowLeft,
  FaArrowRight,
  FaBell,
  FaCheck,
  FaCheckCircle,
  FaClipboardCheck,
  FaCloudSun,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaMicrochip,
  FaMoon,
  FaSatelliteDish,
  FaShieldAlt,
  FaSun,
  FaTint,
} from 'react-icons/fa';
import './OperationalAlertsPreview.css';

const OPERATIONAL_ALERTS = [
  {
    id: 'soil-critical',
    status: 'action',
    severity: 'critical',
    source: 'Irrigação',
    title: 'Umidade abaixo do limite no Talhão Norte',
    description: 'O solo está com 28% de água disponível. A reposição recomendada é de 12,6 mm antes do aumento do vento.',
    property: 'Fazenda Horizonte',
    plot: 'Talhão Norte',
    time: 'Há 18 min',
    metric: '28% · déficit crítico',
    action: 'Planejar irrigação',
    target: 'irrigacao',
    icon: FaTint,
  },
  {
    id: 'wind-window',
    status: 'action',
    severity: 'warning',
    source: 'Previsão',
    title: 'Janela de aplicação termina às 14h',
    description: 'A previsão indica aumento do vento durante a tarde. Antecipe aplicações sensíveis para reduzir deriva e perda operacional.',
    property: 'Fazenda Horizonte',
    plot: 'Todos os talhões',
    time: 'Atualizado há 12 min',
    metric: 'Vento acima de 2,8 m/s',
    action: 'Ver previsão',
    target: 'previsao',
    icon: FaCloudSun,
  },
  {
    id: 'ndvi-watch',
    status: 'watch',
    severity: 'watch',
    source: 'Satélite',
    title: 'Vigor desigual detectado em 2,7 ha',
    description: 'O setor noroeste apresentou vigor abaixo do restante do talhão em duas imagens consecutivas. Recomenda-se vistoria em campo.',
    property: 'Fazenda Horizonte',
    plot: 'Talhão Norte',
    time: 'Imagem de 09 ago',
    metric: '14,7% da área em atenção',
    action: 'Analisar imagem',
    target: 'sensoriamento',
    icon: FaSatelliteDish,
  },
  {
    id: 'sensor-confirmed',
    status: 'resolved',
    severity: 'resolved',
    source: 'Sensores',
    title: 'Evento de chuva confirmado no Setor Sul',
    description: 'A estação registrou 2,8 mm e sincronizou normalmente. O evento foi incorporado ao acompanhamento hídrico.',
    property: 'Fazenda Horizonte',
    plot: 'Talhão Sul',
    time: 'Hoje, 06:20',
    metric: '2,8 mm registrados',
    action: 'Ver sensores',
    target: 'sensores',
    icon: FaMicrochip,
  },
];

const FILTERS = [
  { key: 'all', label: 'Todos' },
  { key: 'action', label: 'Exigem ação' },
  { key: 'watch', label: 'Acompanhar' },
  { key: 'resolved', label: 'Resolvidos' },
];

function AlertCard({ alert, resolved, onResolve, onNavigate, onCreateTask }) {
  const Icon = alert.icon;
  const effectiveStatus = resolved ? 'resolved' : alert.status;

  return (
    <article className={`operational-alert-card is-${effectiveStatus} severity-${alert.severity}`}>
      <div className="operational-alert-card-icon" aria-hidden="true">
        {resolved ? <FaCheckCircle /> : <Icon />}
      </div>

      <div className="operational-alert-card-body">
        <div className="operational-alert-card-topline">
          <span className="operational-alert-source">{alert.source}</span>
          <span className={`operational-alert-status is-${effectiveStatus}`}>
            {effectiveStatus === 'action' && 'Ação hoje'}
            {effectiveStatus === 'watch' && 'Acompanhar'}
            {effectiveStatus === 'resolved' && 'Resolvido'}
          </span>
        </div>

        <h2>{alert.title}</h2>
        <p>{alert.description}</p>

        <div className="operational-alert-context">
          <span><FaMapMarkerAlt /> {alert.plot}</span>
          <span>{alert.time}</span>
          <strong>{alert.metric}</strong>
        </div>

        <div className="operational-alert-actions">
          <button type="button" className="operational-alert-primary" onClick={() => onNavigate(alert.target)}>
            {alert.action} <FaArrowRight />
          </button>
          {!resolved && (
            <>
              <button type="button" className="operational-alert-resolve" onClick={onCreateTask}>
                <FaClipboardCheck /> Criar tarefa
              </button>
              <button type="button" className="operational-alert-resolve" onClick={() => onResolve(alert.id)}>
                <FaCheck /> Marcar como resolvido
              </button>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

export default function OperationalAlertsPreview({ darkMode, setDarkMode, scenario, onNavigate, evaluationMode = false }) {
  const operationActive = scenario === 'operacao';
  const [activeFilter, setActiveFilter] = useState('all');
  const [resolvedIds, setResolvedIds] = useState(() => new Set());

  const alerts = operationActive ? OPERATIONAL_ALERTS : [];
  const alertState = useMemo(() => alerts.map(alert => ({
    ...alert,
    effectiveStatus: resolvedIds.has(alert.id) ? 'resolved' : alert.status,
  })), [alerts, resolvedIds]);

  const actionCount = alertState.filter(alert => alert.effectiveStatus === 'action').length;
  const watchCount = alertState.filter(alert => alert.effectiveStatus === 'watch').length;
  const resolvedCount = alertState.filter(alert => alert.effectiveStatus === 'resolved').length;
  const visibleAlerts = activeFilter === 'all'
    ? alertState
    : alertState.filter(alert => alert.effectiveStatus === activeFilter);

  const resolveAlert = (alertId) => {
    setResolvedIds(current => {
      const next = new Set(current);
      next.add(alertId);
      return next;
    });
  };

  return (
    <div className={`operational-alerts-shell ${darkMode ? 'is-dark' : ''}`}>
      <header className="operational-alerts-topbar">
        <button type="button" className="operational-alerts-back" onClick={() => onNavigate('dashboard')} aria-label="Voltar ao painel">
          <FaArrowLeft />
        </button>
        <div className="operational-alerts-brand">
          <span className="operational-alerts-brandmark"><FaBell /></span>
          <div>
            <strong>Central de Alertas</strong>
            <small>Climorax · decisão operacional</small>
          </div>
        </div>
        <button
          type="button"
          className="operational-alerts-theme"
          onClick={() => setDarkMode(value => !value)}
          aria-label={darkMode ? 'Ativar tema claro' : 'Ativar tema escuro'}
        >
          {darkMode ? <FaSun /> : <FaMoon />}
        </button>
      </header>

      <main className="operational-alerts-main">
        {evaluationMode && (
          <div className="operational-alerts-evaluation" role="status">
            <FaShieldAlt />
            <div>
              <strong>Avaliação integrada à conta real · branch dev_alexandre</strong>
              <span>O login e a navegação são reais, mas os alertas exibidos nesta etapa são demonstrativos. Nenhuma ação desta tela altera os dados da conta ou a produção.</span>
            </div>
          </div>
        )}
        <section className="operational-alerts-hero">
          <div className="operational-alerts-hero-copy">
            <span className="operational-alerts-kicker">Fazenda Horizonte · Três Lagoas, MS</span>
            <h1>{operationActive ? `${actionCount} decisões precisam de atenção hoje` : 'Tudo o que precisa de atenção, em um só lugar'}</h1>
            <p>
              {operationActive
                ? 'A central cruza clima, sensores, irrigação e imagens para ordenar o que deve ser feito primeiro.'
                : 'Ao conectar propriedades e sensores, a Climorax transforma os dados da operação em prioridades claras.'}
            </p>
          </div>

          <div className="operational-alerts-summary" aria-label="Resumo dos alertas">
            <div className="is-critical"><span>Exigem ação</span><strong>{actionCount}</strong><small>para hoje</small></div>
            <div className="is-watch"><span>Acompanhar</span><strong>{watchCount}</strong><small>sem urgência</small></div>
            <div className="is-resolved"><span>Resolvidos</span><strong>{resolvedCount}</strong><small>neste ciclo</small></div>
          </div>
        </section>

        {!operationActive ? (
          <section className="operational-alerts-empty">
            <span className="operational-alerts-empty-icon"><FaBell /></span>
            <span className="operational-alerts-kicker">Primeiro acesso</span>
            <h2>Sua central será montada automaticamente</h2>
            <p>Cadastre uma propriedade e conecte os dados de campo. Os alertas aparecerão com prioridade, contexto e uma ação recomendada.</p>
            <div>
              <button type="button" className="operational-alert-primary" onClick={() => onNavigate('propriedades')}>
                Cadastrar propriedade <FaArrowRight />
              </button>
              <button type="button" className="operational-alert-secondary" onClick={() => onNavigate('sensores')}>
                Conhecer sensores
              </button>
            </div>
          </section>
        ) : (
          <div className="operational-alerts-layout">
            <section className="operational-alerts-feed">
              <div className="operational-alerts-feed-heading">
                <div>
                  <span className="operational-alerts-kicker">Prioridades da operação</span>
                  <h2>Alertas e recomendações</h2>
                </div>
                <div className="operational-alerts-filters" aria-label="Filtrar alertas">
                  {FILTERS.map(filter => (
                    <button
                      type="button"
                      key={filter.key}
                      className={activeFilter === filter.key ? 'is-active' : ''}
                      aria-pressed={activeFilter === filter.key}
                      onClick={() => setActiveFilter(filter.key)}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="operational-alerts-list">
                {visibleAlerts.length > 0 ? visibleAlerts.map(alert => (
                  <AlertCard
                    key={alert.id}
                    alert={alert}
                    resolved={alert.effectiveStatus === 'resolved'}
                    onResolve={resolveAlert}
                    onNavigate={onNavigate}
                    onCreateTask={() => onNavigate('caderno', { fromAlert: alert.id })}
                  />
                )) : (
                  <div className="operational-alerts-filter-empty">
                    <FaCheckCircle />
                    <strong>Nenhum alerta nesta categoria</strong>
                    <span>A operação está em dia neste filtro.</span>
                  </div>
                )}
              </div>
            </section>

            <aside className="operational-alerts-dayplan">
              <span className="operational-alerts-kicker">Plano do dia</span>
              <h2>Sequência recomendada</h2>
              <p>Ordem sugerida para reduzir risco e aproveitar a janela climática.</p>

              <ol>
                <li>
                  <span>1</span>
                  <div><strong>Programar irrigação</strong><small>Talhão Norte · antes das 14h</small></div>
                  <button type="button" onClick={() => onNavigate('irrigacao')} aria-label="Abrir irrigação"><FaArrowRight /></button>
                </li>
                <li>
                  <span>2</span>
                  <div><strong>Revisar janela climática</strong><small>Aplicações sensíveis · manhã</small></div>
                  <button type="button" onClick={() => onNavigate('previsao')} aria-label="Abrir previsão"><FaArrowRight /></button>
                </li>
                <li>
                  <span>3</span>
                  <div><strong>Vistoriar setor noroeste</strong><small>2,7 ha em atenção</small></div>
                  <button type="button" onClick={() => onNavigate('sensoriamento')} aria-label="Abrir Central de Operações"><FaArrowRight /></button>
                </li>
              </ol>

              <div className="operational-alerts-dayplan-note">
                <FaExclamationTriangle />
                <div><strong>Próxima atualização</strong><span>Dados consolidados novamente em 18 minutos.</span></div>
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}
